import {
    featureDisplayState,
    isExcludedState,
    type FeatureResultState,
    type ProjectedFeature,
} from './project-features.js';
import { scoreToBand, type PreflightScoreBandCode } from './scoring.js';

/**
 * Score attribution: which criteria account for the points a creative did NOT earn.
 *
 * DS emits the asset `score` and the per-feature `weight` / `feature_score` independently, and
 * nothing guarantees the score is the weighted mean of the features — so an absolute per-criterion
 * point cost cannot be computed here. Instead the OBSERVED deficit (`100 − score`) is distributed
 * across criteria in proportion to `weight × (1 − featureScore)`. Bars therefore always sum to the
 * real deficit and can never contradict the headline score, at the cost of being a proportional
 * attribution rather than DS's actual arithmetic. Present it as where the deficit is concentrated,
 * never as an exact penalty.
 *
 * Lives here rather than in the web app so the report UI, the client-facing exports and the
 * server-side PDF can share one definition, as `scoring.ts` already does for bands and blockers.
 */

/** Attributed rows kept before the tail collapses into a single combined row. */
export const MAX_ATTRIBUTION_ROWS = 8;

/** Below this many points a deficit mismatch is rounding noise, not a real unattributed gap. */
const REMAINDER_EPSILON = 0.5;

/** Points above which a criterion earns a row of its own. */
const POINTS_EPSILON = 1e-9;

export type AttributionFeatureInput = Pick<
    ProjectedFeature,
    'code' | 'name' | 'definition' | 'weight' | 'featureScore' | 'state'
>;

export interface AttributionAssetInput {
    features: readonly AttributionFeatureInput[];
    /** 0–100. Assets with no score contribute nothing. */
    score: number | null;
    /** Unanalyzed criteria present — caps the projected band at NEEDS_REVIEW. */
    hasUnanalyzed: boolean;
}

/** One criterion's share of the deficit, averaged across the creatives it was judged on. */
export interface CriterionAttribution {
    /** Stable identity: catalogue code when resolved, else the DS feature name. */
    key: string;
    code: string | null;
    name: string;
    definition: string | null;
    /** Points of the observed deficit attributed to this criterion. */
    points: number;
    /**
     * Heaviest weight observed for this criterion, as a percent (see
     * {@link normalizeFeatureWeight}). Stored already-normalised so a caller never has to guess
     * whether it holds a fraction or a percent.
     */
    weightPercent: number;
    failedCount: number;
    partialCount: number;
    passedCount: number;
    /** Creatives that contributed a verdict for this criterion (the denominator). */
    scoredAssets: number;
    /** The collapsed tail row — a sum of lesser criteria, so not individually actionable. */
    isTail: boolean;
    /** Criteria folded into this row; 0 for an ordinary row. */
    tailCount: number;
}

export interface ScoreAttribution {
    /** Baseline the deficit was measured from. */
    baseScore: number;
    /** `100 − baseScore`. */
    observedGap: number;
    /** Sum of `rows[].points`. */
    attributedTotal: number;
    /**
     * Deficit no criterion could account for — a score below 100 whose criteria all passed.
     * Surfaced to users rather than folded into the last bar.
     */
    unattributedRemainder: number;
    /** Ranked by attributed points, descending; the tail row (when present) is last. */
    rows: CriterionAttribution[];
    /** Any contributing creative had unanalyzed criteria. */
    hasUnanalyzed: boolean;
}

export interface ScoreProjection {
    /** Baseline plus the selected criteria's points, clamped to 100. */
    score: number;
    band: PreflightScoreBandCode;
    /** Points actually gained after clamping. */
    gained: number;
}

const EMPTY_ATTRIBUTION: ScoreAttribution = {
    baseScore: 0,
    observedGap: 0,
    attributedTotal: 0,
    unattributedRemainder: 0,
    rows: [],
    hasUnanalyzed: false,
};

/**
 * DS weights arrive as a 0–1 fraction or as an already-multiplied percent. Normalise to a percent
 * so a payload that mixes the two cannot skew one criterion's share. Mirrors the display-side
 * `weightPercent` rule in the web app.
 */
export function normalizeFeatureWeight(weight: number): number {
    if (!Number.isFinite(weight) || weight <= 0) return 0;
    return weight <= 1 ? weight * 100 : weight;
}

function clamp(value: number, min: number, max: number): number {
    return Math.min(max, Math.max(min, value));
}

/** Criteria that carry a verdict AND a weight — anything else cannot move the score. */
function participates(feature: AttributionFeatureInput): boolean {
    return (
        !isExcludedState(featureDisplayState(feature)) &&
        normalizeFeatureWeight(feature.weight) > 0
    );
}

function criterionKey(feature: AttributionFeatureInput): string {
    return feature.code ?? feature.name;
}

/** One criterion's slice of a single creative's deficit. */
export interface AssetContribution {
    feature: AttributionFeatureInput;
    key: string;
    state: FeatureResultState;
    points: number;
}

/**
 * Distribute one creative's deficit across its participating criteria. Returns a contribution per
 * participating criterion — including zero-point ones, so a criterion that passed here and failed
 * elsewhere still reports an accurate denominator upstream.
 */
export function attributeAssetDeficit(
    features: readonly AttributionFeatureInput[],
    score: number,
): AssetContribution[] {
    const participating = features.filter(participates);
    if (participating.length === 0) return [];

    const weightBase = participating.reduce(
        (sum, feature) => sum + normalizeFeatureWeight(feature.weight),
        0,
    );
    if (weightBase <= 0) return [];

    const gap = clamp(100 - score, 0, 100);
    const raw = participating.map((feature) => {
        const share = normalizeFeatureWeight(feature.weight) / weightBase;
        const earned = clamp(feature.featureScore ?? 0, 0, 1);
        return { feature, raw: share * (1 - earned) };
    });
    const rawTotal = raw.reduce((sum, entry) => sum + entry.raw, 0);

    return raw.map(({ feature, raw: rawContribution }) => ({
        feature,
        key: criterionKey(feature),
        state: featureDisplayState(feature),
        // rawTotal of 0 means every criterion passed: the gap (if any) is unattributable.
        points: rawTotal > 0 ? (rawContribution / rawTotal) * gap : 0,
    }));
}

type AttributionAccumulator = Omit<CriterionAttribution, 'isTail' | 'tailCount'>;

/**
 * Aggregate attribution across a report's scored creatives, measured from `baseScore` (the
 * report's overall score). Each creative is attributed on its own before averaging — averaging
 * feature scores first would let a criterion that failed once and passed three times read as
 * uniformly mediocre.
 */
export function attributeReportDeficit(
    assets: readonly AttributionAssetInput[],
    baseScore: number | null,
): ScoreAttribution {
    const scored = assets.filter((asset) => asset.score != null);
    if (baseScore == null || scored.length === 0) return EMPTY_ATTRIBUTION;

    const byCriterion = new Map<string, AttributionAccumulator>();

    for (const asset of scored) {
        for (const contribution of attributeAssetDeficit(asset.features, asset.score as number)) {
            const entry =
                byCriterion.get(contribution.key) ??
                ({
                    key: contribution.key,
                    code: contribution.feature.code,
                    name: contribution.feature.name,
                    definition: contribution.feature.definition,
                    points: 0,
                    weightPercent: 0,
                    failedCount: 0,
                    partialCount: 0,
                    passedCount: 0,
                    scoredAssets: 0,
                } satisfies AttributionAccumulator);

            if (!entry.definition && contribution.feature.definition) {
                entry.definition = contribution.feature.definition;
            }
            entry.points += contribution.points;
            entry.weightPercent = Math.max(
                entry.weightPercent,
                normalizeFeatureWeight(contribution.feature.weight),
            );
            entry.scoredAssets += 1;
            if (contribution.state === 'FAIL') entry.failedCount += 1;
            else if (contribution.state === 'PARTIAL') entry.partialCount += 1;
            else if (contribution.state === 'PASS') entry.passedCount += 1;
            byCriterion.set(contribution.key, entry);
        }
    }

    const observedGap = clamp(100 - baseScore, 0, 100);
    const averaged = [...byCriterion.values()].map((entry) => ({
        ...entry,
        points: entry.points / scored.length,
    }));

    // Averaging per-creative deficits can overshoot the report's own gap, because the report
    // score is not guaranteed to be the plain mean of its creatives'. Scale back so the bars
    // never claim more missing points than the headline score says are missing.
    const averagedTotal = averaged.reduce((sum, entry) => sum + entry.points, 0);
    const scale =
        averagedTotal > observedGap && averagedTotal > 0 ? observedGap / averagedTotal : 1;

    const ranked = averaged
        .map((entry) => ({ ...entry, points: entry.points * scale }))
        .filter((entry) => entry.points > POINTS_EPSILON)
        .sort((a, b) => b.points - a.points || b.failedCount - a.failedCount);

    const rows = collapseTail(ranked);
    const attributedTotal = rows.reduce((sum, row) => sum + row.points, 0);
    const remainder = observedGap - attributedTotal;

    return {
        baseScore,
        observedGap,
        attributedTotal,
        unattributedRemainder: remainder > REMAINDER_EPSILON ? remainder : 0,
        rows,
        hasUnanalyzed: scored.some((asset) => asset.hasUnanalyzed),
    };
}

/** Keep the top rows individually and sum the rest, so a 16-criterion report stays scannable. */
function collapseTail(ranked: readonly AttributionAccumulator[]): CriterionAttribution[] {
    if (ranked.length <= MAX_ATTRIBUTION_ROWS) {
        return ranked.map((entry) => ({ ...entry, isTail: false, tailCount: 0 }));
    }

    const kept = ranked
        .slice(0, MAX_ATTRIBUTION_ROWS)
        .map((entry) => ({ ...entry, isTail: false, tailCount: 0 }));
    const tail = ranked.slice(MAX_ATTRIBUTION_ROWS);

    return [
        ...kept,
        {
            key: '__tail__',
            code: null,
            // Presentation resolves the label — this module holds no user-facing copy.
            name: '',
            definition: null,
            points: tail.reduce((sum, entry) => sum + entry.points, 0),
            weightPercent: Math.max(...tail.map((entry) => entry.weightPercent)),
            failedCount: tail.reduce((sum, entry) => sum + entry.failedCount, 0),
            partialCount: tail.reduce((sum, entry) => sum + entry.partialCount, 0),
            passedCount: tail.reduce((sum, entry) => sum + entry.passedCount, 0),
            scoredAssets: Math.max(...tail.map((entry) => entry.scoredAssets)),
            isTail: true,
            tailCount: tail.length,
        },
    ];
}

/**
 * Project the score if the given points were recovered. The band runs through {@link scoreToBand}
 * with the report's `hasUnanalyzed` flag so a projection can never promise a STRONG badge the
 * product would then decline to show.
 */
export function projectScore(
    baseScore: number,
    gainedPoints: number,
    hasUnanalyzed: boolean,
): ScoreProjection {
    const score = clamp(baseScore + Math.max(0, gainedPoints), 0, 100);
    return {
        score,
        band: scoreToBand(score, hasUnanalyzed),
        gained: score - baseScore,
    };
}

/** Points recoverable by fixing every individually-actionable row (the tail is excluded). */
export function actionableCeiling(attribution: ScoreAttribution): number {
    return attribution.rows
        .filter((row) => !row.isTail)
        .reduce((sum, row) => sum + row.points, 0);
}
